import { useEffect, useState } from 'react';
import { useAuth } from '../context/AuthContext';

/**
 * AccessDeniedPage - Shown when Auth0 denies access (e.g., post-login action)
 * Prevents infinite redirect loop by NOT auto-redirecting to login
 */
function AccessDeniedPage() {
  const { authConfig } = useAuth();
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Extract error details from URL
    const params = new URLSearchParams(window.location.search);
    const description = params.get('error_description') || 
                       params.get('message') || 
                       'Your account has been denied access. Please contact support.';
    setErrorMessage(description);
  }, []);

  const handleTryAgain = () => {
    if (!authConfig) return;
    
    // Force Auth0 to show login screen with prompt=login
    // This allows user to choose a different account
    const redirectUri = encodeURIComponent(authConfig.redirect_uri);
    const responseType = 'code';
    const scope = encodeURIComponent('openid profile email');
    
    const authUrl = `https://${authConfig.auth0_domain}/authorize?` +
      `response_type=${responseType}&` +
      `client_id=${authConfig.client_id}&` +
      `redirect_uri=${redirectUri}&` +
      `scope=${scope}&` +
      `prompt=login`; // Force login screen even if session exists
    
    window.location.href = authUrl;
  };

  return (
    <div style={{
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      minHeight: '100vh',
      padding: '40px 20px',
      background: 'linear-gradient(135deg, #878787ff 0%, #ffffffff 100%)'
    }}>
      <div style={{
        maxWidth: '600px',
        background: 'white',
        borderRadius: '12px',
        padding: '40px',
        boxShadow: '0 4px 6px rgba(0, 0, 0, 0.07), 0 10px 20px rgba(0, 0, 0, 0.06)',
        textAlign: 'center'
      }}>
        <img 
          src="/lendport_logo_icon_text.svg" 
          alt="Lendport" 
          style={{ 
            width: '140px', 
            opacity: 0.4,
            filter: 'grayscale(100%)',
            marginBottom: '30px'
          }} 
        />
        
        <div style={{
          width: '80px',
          height: '80px',
          borderRadius: '50%',
          background: '#fef2f2',
          border: '3px solid #ef4444',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          margin: '0 auto 25px',
          position: 'relative'
        }}>
          <svg width="40" height="40" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
            <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-2h2v2zm0-4h-2V7h2v6z" fill="#ef4444"/>
          </svg>
        </div>

        <h1 style={{ 
          fontSize: '24px', 
          fontWeight: '600', 
          color: '#1f2937', 
          marginBottom: '16px' 
        }}>
          Access Denied
        </h1>

        <p style={{ 
          fontSize: '15px', 
          color: '#6b7280', 
          lineHeight: '1.6',
          marginBottom: '30px' 
        }}>
          {errorMessage}
        </p>

        <div style={{
          background: '#f9fafb',
          border: '1px solid #e5e7eb',
          borderRadius: '8px',
          padding: '20px',
          marginBottom: '30px',
          textAlign: 'left'
        }}>
          <h3 style={{ 
            fontSize: '14px', 
            fontWeight: '600', 
            color: '#374151', 
            marginBottom: '12px' 
          }}>
            What you can do:
          </h3>
          <ul style={{ 
            fontSize: '13px', 
            color: '#6b7280', 
            lineHeight: '1.8',
            margin: 0,
            paddingLeft: '20px'
          }}>
            <li>Contact Lendport support to request access provisioning</li>
            <li>Verify you're using an approved email address</li>
            <li>Try logging in with a different account if you have one</li>
          </ul>
        </div>

        <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
          <button
            onClick={handleTryAgain}
            style={{
              padding: '12px 24px',
              background: '#3b82f6',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '14px',
              fontWeight: '500',
              transition: 'background 0.2s'
            }}
            onMouseOver={(e) => e.target.style.background = '#2563eb'}
            onMouseOut={(e) => e.target.style.background = '#3b82f6'}
          >
            Try Different Account
          </button>
        </div>

        <p style={{ 
          fontSize: '12px', 
          color: '#9ca3af', 
          marginTop: '20px' 
        }}>
          Need help? Contact support at <a href="mailto:support@lendport.com" style={{ color: '#3b82f6', textDecoration: 'none' }}>support@lendport.com</a>
        </p>
      </div>
    </div>
  );
}

export default AccessDeniedPage;
